import React from 'react';
import { RecommendedUsersLogicReturn, PaginationButton } from '../RecommendedUsers.types';
import '../RecommendedUsers.css';

interface BasicRecommendedUsersSkinProps extends RecommendedUsersLogicReturn {
  // 스킨 옵션
  title?: string;
  emptyText?: string;
  loadingText?: string;
  loginRequiredText?: string;
  showTotalCount?: boolean;
  showUserIds?: boolean;
  showUserNames?: boolean;
  showCreatedDate?: boolean;
  showPagination?: boolean;
  listLayout?: 'card' | 'list' | 'table';
  showAvatars?: boolean;
  compactMode?: boolean;
  
  // 색상 테마
  backgroundColor?: string;
  borderColor?: string;
  textColor?: string;
  titleColor?: string;
  primaryColor?: string;
  secondaryColor?: string;
  successColor?: string;
  
  // 아이템 스타일
  itemBackgroundColor?: string;
  itemBorderColor?: string;
  itemHoverColor?: string;
  itemShadow?: string;
  itemBorderRadius?: string;
  itemPadding?: string;
  itemMargin?: string;
  
  // 타이틀 스타일
  titleFontSize?: string;
  titleFontWeight?: string;
  titleMarginBottom?: string;
  
  // 텍스트 스타일
  nameFontSize?: string;
  nameFontWeight?: string;
  nameColor?: string;
  dateFontSize?: string;
  dateColor?: string;
  idFontSize?: string;
  idColor?: string;
  
  // 페이지네이션 스타일
  paginationButtonColor?: string;
  paginationButtonBorderColor?: string;
  paginationActiveColor?: string;
  paginationActiveTextColor?: string;
  paginationHoverColor?: string;
  
  // 로딩 및 상태
  loadingSpinnerColor?: string;
  errorColor?: string;
  emptyStateColor?: string;
}

const BasicRecommendedUsersSkin: React.FC<BasicRecommendedUsersSkinProps> = ({
  // Logic props
  userInfo,
  items,
  allItems,
  loading,
  error,
  pagination,
  currentPage,
  limit,
  searchQuery,
  handlePageChange,
  handleSearch,
  clearSearch,
  handleRefresh,
  formatDate,
  generatePaginationButtons,
  t,
  renderCount,
  
  // Skin options
  title = '피추천인 목록',
  emptyText = '데이터가 없습니다',
  loadingText = '로딩 중...',
  loginRequiredText = '로그인이 필요합니다.',
  showTotalCount = true,
  showUserIds = true,
  showUserNames = true,
  showCreatedDate = true,
  showPagination = true,
  listLayout = 'card',
  showAvatars = false,
  compactMode = false,
  
  // 색상 테마
  backgroundColor = '#ffffff',
  borderColor = '#e0e0e0',
  textColor = '#333333',
  titleColor = '#2c3e50',
  primaryColor = '#3f51b5',
  secondaryColor = '#6c757d',
  successColor = '#28a745',
  
  // 아이템 스타일
  itemBackgroundColor = '#ffffff',
  itemBorderColor = '#e0e0e0',
  itemHoverColor = '#f8f9fa',
  itemShadow = '0 2px 4px rgba(0,0,0,0.1)',
  itemBorderRadius = '8px',
  itemPadding = '16px',
  itemMargin = '8px',
  
  // 타이틀 스타일
  titleFontSize = '24px',
  titleFontWeight = 'bold',
  titleMarginBottom = '20px',
  
  // 텍스트 스타일
  nameFontSize = '16px',
  nameFontWeight = '600',
  nameColor = '#2c3e50',
  dateFontSize = '14px',
  dateColor = '#6c757d',
  idFontSize = '14px',
  idColor = '#495057',
  
  // 페이지네이션 스타일
  paginationButtonColor = '#ffffff',
  paginationButtonBorderColor = '#dee2e6',
  paginationActiveColor = '#3f51b5',
  paginationActiveTextColor = '#ffffff',
  paginationHoverColor = '#f8f9fa',
  
  // 로딩 및 상태
  loadingSpinnerColor = '#3f51b5',
  errorColor = '#dc3545',
  emptyStateColor = '#6c757d'
}) => {
  // 컨테이너 스타일
  const containerStyle = {
    backgroundColor,
    border: `1px solid ${borderColor}`,
    borderRadius: itemBorderRadius,
    padding: compactMode ? '12px' : '20px',
    color: textColor
  };
  
  // 타이틀 스타일
  const titleStyle = {
    fontSize: titleFontSize,
    fontWeight: titleFontWeight,
    color: titleColor,
    marginBottom: titleMarginBottom,
    margin: 0
  };
  
  // 아이템 스타일 함수
  const getItemStyle = (isHovered = false) => ({
    backgroundColor: isHovered ? itemHoverColor : itemBackgroundColor,
    border: `1px solid ${itemBorderColor}`,
    borderRadius: itemBorderRadius,
    padding: itemPadding,
    margin: itemMargin,
    boxShadow: itemShadow,
    transition: 'all 0.2s ease',
    cursor: 'default'
  });
  
  // 검색 컴포넌트
  const SearchBar = () => (
    <div style={{ marginBottom: '16px', display: 'flex', gap: '8px', alignItems: 'center' }}>
      <input
        type="text"
        placeholder={t('사용자 이름 또는 ID로 검색...')}
        value={searchQuery}
        onChange={(e) => handleSearch(e.target.value)}
        style={{
          flex: 1,
          padding: '8px 12px',
          border: `1px solid ${borderColor}`,
          borderRadius: '4px',
          fontSize: '14px'
        }}
      />
      {searchQuery && (
        <button
          onClick={clearSearch}
          style={{
            padding: '8px 12px',
            backgroundColor: secondaryColor,
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '14px'
          }}
        >
          {t('초기화')}
        </button>
      )}
      <button
        onClick={handleRefresh}
        style={{
          padding: '8px 12px',
          backgroundColor: primaryColor,
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          cursor: 'pointer',
          fontSize: '14px'
        }}
      >
        {t('새로고침')}
      </button>
    </div>
  );
  
  // 아이템 렌더링 함수
  const renderItem = (user: any, index: number) => {
    // Hook을 컴포넌트 레벨로 이동
    const isHovered = false; // 임시로 false로 설정
    
    if (listLayout === 'table') {
      return (
        <tr
          key={user.id}
          style={{
            backgroundColor: isHovered ? itemHoverColor : 'transparent',
            transition: 'background-color 0.2s ease'
          }}
          onMouseEnter={() => {}}
          onMouseLeave={() => {}}
        >
          {showUserIds && (
            <td style={{ padding: '12px', borderBottom: `1px solid ${borderColor}`, fontSize: idFontSize, color: idColor }}>
              {user.insungId}
            </td>
          )}
          {showUserNames && (
            <td style={{ padding: '12px', borderBottom: `1px solid ${borderColor}`, fontSize: nameFontSize, fontWeight: nameFontWeight, color: nameColor }}>
              {user.name}
            </td>
          )}
          {showCreatedDate && (
            <td style={{ padding: '12px', borderBottom: `1px solid ${borderColor}`, fontSize: dateFontSize, color: dateColor }}>
              {formatDate(user.createdAt)}
            </td>
          )}
        </tr>
      );
    }
    
    if (listLayout === 'list') {
      return (
        <div
          key={user.id}
          style={{
            ...getItemStyle(isHovered),
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
          onMouseEnter={() => {}}
          onMouseLeave={() => {}}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {showAvatars && (
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                backgroundColor: primaryColor,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontWeight: 'bold'
              }}>
                {user.name ? user.name.charAt(0).toUpperCase() : 'U'}
              </div>
            )}
            <div>
              {showUserNames && (
                <div style={{ fontSize: nameFontSize, fontWeight: nameFontWeight, color: nameColor }}>
                  {user.name}
                </div>
              )}
              {showUserIds && (
                <div style={{ fontSize: idFontSize, color: idColor }}>
                  ID: {user.insungId}
                </div>
              )}
            </div>
          </div>
          {showCreatedDate && (
            <div style={{ fontSize: dateFontSize, color: dateColor }}>
              {formatDate(user.createdAt)}
            </div>
          )}
        </div>
      );
    }
    
    // Default card layout
    return (
      <div
        key={user.id}
        style={getItemStyle(isHovered)}
        onMouseEnter={() => {}}
        onMouseLeave={() => {}}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: showCreatedDate ? '8px' : '0' }}>
          {showAvatars && (
            <div style={{
              width: compactMode ? '32px' : '48px',
              height: compactMode ? '32px' : '48px',
              borderRadius: '50%',
              backgroundColor: primaryColor,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontWeight: 'bold',
              fontSize: compactMode ? '14px' : '18px'
            }}>
              {user.name ? user.name.charAt(0).toUpperCase() : 'U'}
            </div>
          )}
          <div style={{ flex: 1 }}>
            {showUserNames && (
              <div style={{ fontSize: nameFontSize, fontWeight: nameFontWeight, color: nameColor, marginBottom: '4px' }}>
                {user.name}
              </div>
            )}
            {showUserIds && (
              <div style={{ fontSize: idFontSize, color: idColor }}>
                ID: {user.insungId}
              </div>
            )}
          </div>
        </div>
        {showCreatedDate && (
          <div style={{ fontSize: dateFontSize, color: dateColor, textAlign: 'right' }}>
            {formatDate(user.createdAt)}
          </div>
        )}
      </div>
    );
  };
  
  // 페이지네이션 렌더링
  const renderPagination = () => {
    if (!showPagination || pagination.totalPages <= 1) return null;
    
    const buttons = generatePaginationButtons();
    
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        gap: '4px', 
        marginTop: '20px',
        flexWrap: 'wrap'
      }}>
        {buttons.map((button, index) => (
          <button
            key={`${button.type}-${button.page}-${index}`}
            onClick={() => !button.disabled && handlePageChange(button.page)}
            disabled={button.disabled}
            style={{
              padding: '8px 12px',
              border: `1px solid ${paginationButtonBorderColor}`,
              backgroundColor: button.active ? paginationActiveColor : paginationButtonColor,
              color: button.active ? paginationActiveTextColor : textColor,
              borderRadius: '4px',
              cursor: button.disabled ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              minWidth: '40px',
              opacity: button.disabled ? 0.5 : 1,
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              if (!button.disabled && !button.active) {
                e.currentTarget.style.backgroundColor = paginationHoverColor;
              }
            }}
            onMouseLeave={(e) => {
              if (!button.disabled && !button.active) {
                e.currentTarget.style.backgroundColor = paginationButtonColor;
              }
            }}
          >
            {button.label}
          </button>
        ))}
      </div>
    );
  };
  
  // 로그인이 안된 경우
  if (!userInfo) {
    return (
      <div style={containerStyle}>
        <h2 style={titleStyle}>{t(title)}</h2>
        <div style={{ 
          textAlign: 'center', 
          padding: '40px', 
          color: emptyStateColor,
          fontSize: '16px'
        }}>
          <p>{t(loginRequiredText)}</p>
        </div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <h2 style={titleStyle}>{t(title)}</h2>
      
      <SearchBar />
      
      {loading && (
        <div style={{ 
          textAlign: 'center', 
          padding: '40px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '16px'
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: `4px solid ${borderColor}`,
            borderTop: `4px solid ${loadingSpinnerColor}`,
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }} />
          <p style={{ color: textColor, fontSize: '16px' }}>{t(loadingText)}</p>
        </div>
      )}
      
      {error && (
        <div style={{ 
          textAlign: 'center', 
          padding: '20px',
          backgroundColor: '#fef2f2',
          border: `1px solid ${errorColor}`,
          borderRadius: itemBorderRadius,
          marginBottom: '20px'
        }}>
          <p style={{ color: errorColor, fontSize: '16px' }}>{t(error)}</p>
        </div>
      )}
      
      {!loading && !error && (
        <>
          {showTotalCount && (
            <div style={{ 
              marginBottom: '16px', 
              color: secondaryColor, 
              fontSize: '14px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <span>
                {searchQuery ? 
                  `${t('검색 결과')}: ${items.length}${t('명')} / ${t('전체')}: ${pagination.totalCount}${t('명')}` :
                  `${t('총')} ${pagination.totalCount} ${t('명')}`
                }
              </span>
              {searchQuery && (
                <span style={{ fontSize: '12px', color: dateColor }}>
                  "{searchQuery}" {t('검색 중')}
                </span>
              )}
            </div>
          )}
          
          {items.length === 0 ? (
            <div style={{ 
              textAlign: 'center', 
              padding: '40px',
              color: emptyStateColor,
              fontSize: '16px'
            }}>
              <p>{searchQuery ? t('검색 결과가 없습니다') : t(emptyText)}</p>
            </div>
          ) : (
            <>
              {listLayout === 'table' ? (
                <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '20px' }}>
                  <thead>
                    <tr style={{ backgroundColor: itemHoverColor }}>
                      {showUserIds && (
                        <th style={{ 
                          padding: '12px', 
                          textAlign: 'left', 
                          borderBottom: `2px solid ${borderColor}`,
                          fontSize: '14px',
                          fontWeight: '600',
                          color: titleColor
                        }}>
                          {t('사용자 ID')}
                        </th>
                      )}
                      {showUserNames && (
                        <th style={{ 
                          padding: '12px', 
                          textAlign: 'left', 
                          borderBottom: `2px solid ${borderColor}`,
                          fontSize: '14px',
                          fontWeight: '600',
                          color: titleColor
                        }}>
                          {t('이름')}
                        </th>
                      )}
                      {showCreatedDate && (
                        <th style={{ 
                          padding: '12px', 
                          textAlign: 'left', 
                          borderBottom: `2px solid ${borderColor}`,
                          fontSize: '14px',
                          fontWeight: '600',
                          color: titleColor
                        }}>
                          {t('가입일')}
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((user, index) => renderItem(user, index))}
                  </tbody>
                </table>
              ) : (
                <div style={{ 
                  display: listLayout === 'list' ? 'flex' : 'grid', 
                  flexDirection: listLayout === 'list' ? 'column' : undefined,
                  gridTemplateColumns: listLayout === 'card' ? 
                    (compactMode ? 'repeat(auto-fill, minmax(280px, 1fr))' : 'repeat(auto-fill, minmax(320px, 1fr))') : 
                    undefined,
                  gap: compactMode ? '8px' : '12px',
                  marginBottom: '20px'
                }}>
                  {items.map((user, index) => renderItem(user, index))}
                </div>
              )}
            </>
          )}
          
          {renderPagination()}
        </>
      )}
      
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

export default BasicRecommendedUsersSkin;